import { sift } from 'radash';
import { convertCentsToUnit, format } from '@monofront/business/utils';
import UltraFresh from '@monofront/design-system/visuals/Pictograms/UltraFresh';
import { useTranslations } from 'next-intl';
import { formatShippingMethodsThresholds } from './helpers';
import { FormattedData, ThresholdData, ThresholdPriceData } from './types';
import { DeliveryBarMachineContextShippingMethod } from '@monofront/business/stateMachines/deliveryBar/types';
import useMinimumOrderAmount from '@hooks/useMinimumOrderAmount';

const Td = ({ children }) => (
  <td className="tw-py-1.5 tw-text-center first:tw-text-left">{children}</td>
);

const Row = ({
  rowIndex,
  tableLength,
  rowElements,
  previousRowElements,
}: {
  rowIndex: number;
  tableLength: number;
  rowElements: FormattedData;
  previousRowElements: FormattedData | undefined;
}) => {
  const tSlotTable = useTranslations(
    'DeliveryBar.ShippingMethodSelection.Table',
  );

  if (!rowElements) return null;

  const isLastRow = rowIndex === tableLength - 1;

  const thresholdCol = rowElements[0] as ThresholdData;
  const previousThresholdCol = previousRowElements?.[0] as ThresholdData;
  const currentThreshold = thresholdCol.value;
  const previousThreshold = previousThresholdCol?.value;

  const values = sift(
    rowElements.filter((el) => el?.accessorKey !== 'threshold'),
  ) as ThresholdPriceData[];

  return (
    <tr>
      <Td>
        {isLastRow
          ? tSlotTable.rich('greaterThanPrice', {
              value: format(convertCentsToUnit(previousThreshold), {
                minimumFractionDigits: 0,
              }),
            })
          : tSlotTable.rich('fromPriceToPrice', {
              fromValue: format(convertCentsToUnit(previousThreshold), {
                minimumFractionDigits: 0,
              }),
              toValue: format(convertCentsToUnit(currentThreshold), {
                minimumFractionDigits: 0,
              }),
            })}
      </Td>
      {values.map((element, index) => {
        if (!element) return;

        return (
          <Td key={index}>
            {element.underThresholdAmount === 0
              ? tSlotTable('free')
              : format(convertCentsToUnit(element.underThresholdAmount))}
          </Td>
        );
      })}
    </tr>
  );
};

export const SlotsTable: React.FC<{
  shippingMethod: DeliveryBarMachineContextShippingMethod | null;
  UNSAFE_className?: string;
}> = ({ shippingMethod = null, UNSAFE_className = '' }) => {
  const tSlotTable = useTranslations(
    'DeliveryBar.ShippingMethodSelection.Table',
  );
  const { minimumOrderAmount } = useMinimumOrderAmount();
  const minimumOrderAmountInCents = minimumOrderAmount * 100;

  if (!shippingMethod) return null;

  const { thresholdCol, standardThresholds, ultraFreshThresholds } =
    formatShippingMethodsThresholds(shippingMethod, minimumOrderAmountInCents);

  return (
    <table
      className={`tw-lf-body-regular-sm tw-w-full tw-border-collapse${
        UNSAFE_className ? ` ${UNSAFE_className}` : ''
      }`}
      data-testId="Deliverybar-slots-table"
    >
      {Boolean(standardThresholds.length && ultraFreshThresholds.length) && (
        <thead>
          <tr>
            <th className="tw-pb-2" />
            <th className="tw-pb-2">
              <span className="tw-flex tw-items-center tw-justify-center">
                {tSlotTable('withoutUF')} <UltraFresh />
              </span>
            </th>
            <th className="tw-pb-2">
              <span className="tw-flex tw-items-center tw-justify-center">
                {tSlotTable('withUF')} <UltraFresh />
              </span>
            </th>
          </tr>
        </thead>
      )}
      <tbody
        className="[&>*:nth-child(odd)]:tw-bg-blue-100"
        data-testid="DeliveryBar-PricesTable"
      >
        {thresholdCol.map((_, index) => {
          const rowElements = [
            thresholdCol[index],
            standardThresholds[index],
            ultraFreshThresholds[index],
          ];

          const previousRowElements =
            index > 0
              ? [
                  thresholdCol[index - 1],
                  standardThresholds[index - 1],
                  ultraFreshThresholds[index - 1],
                ]
              : undefined;

          return (
            previousRowElements && (
              <Row
                key={index}
                rowIndex={index}
                previousRowElements={previousRowElements}
                rowElements={rowElements}
                tableLength={thresholdCol.length}
              />
            )
          );
        })}
      </tbody>
    </table>
  );
};
